import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Your style → Preferences in depth.
// Fixture: `preferencesInDepth` (the demo officer, preferences already set, Valley Mart in the list). Goes past
// the "Set up your report preferences" video: what each group of fields does
// to the draft, the Common Spellings list, saving, and how to check a change.
// Nothing here changes the officer's real data: it all lives in the fixture.
const field = (key: string) => `${sel("preferenceField")}[data-pref="${key}"]`;
const trigger = (key: string) => `${field(key)} .dropdown > button`;
const option = (key: string, n: number) => `${field(key)} .dropdown-content li:nth-child(${n}) p`;
const current = (key: string) => `${field(key)} .dropdown-content li p.bg-base-300`;
const spellings = `${field("common_spellings")} input[aria-label="List item input"]`;

const scene: Scene = {
  id: "report-preferences-in-depth",
  title: "Preferences in depth",
  startRoute: "/preferences",
  fixture: "preferencesInDepth",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Every field on Report Preferences changes something specific in the draft. Let's go through them in groups, starting with how the report refers to you.",
      actions: [
        { type: "waitFor", target: sel("preferencesForm") },
        { type: "waitFor", target: field("name") },
        { type: "wait", ms: 4600 },
        { type: "spotlight", target: field("name") },
        { type: "wait", ms: 2500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "identity",
      narration:
        "Include Name decides whether the report uses your name, and Include Badge Number adds your badge after it. Include Rank or Title is the word in front of your name. Leave it blank and you are Officer.",
      actions: [
        { type: "spotlight", target: field("name") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: field("badge_number") },
        { type: "wait", ms: 3200 },
        { type: "scrollIntoView", target: field("rank") },
        { type: "spotlight", target: field("rank") },
        { type: "wait", ms: 4500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "rank-style",
      narration:
        "Abbreviate Rank turns Officer into Ofc, and Capitalize Rank writes it in capitals. Capitalize Names does the same for subjects and witnesses, so Reyes becomes REYES.",
      actions: [
        { type: "scrollIntoView", target: field("abbreviate_rank") },
        { type: "spotlight", target: field("abbreviate_rank") },
        { type: "wait", ms: 3500 },
        { type: "spotlight", target: field("capitalize_rank") },
        { type: "wait", ms: 2800 },
        { type: "spotlight", target: field("capitalize_names") },
        { type: "wait", ms: 4500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "voice",
      narration:
        "Narrative Perspective is first person, I contacted, or third person, Officer Smith contacted. Subject Reference Style decides how every person is named after the first mention: by role, surname, or full name.",
      actions: [
        { type: "scrollIntoView", target: field("perspective") },
        { type: "spotlight", target: field("perspective") },
        { type: "wait", ms: 6000 },
        { type: "spotlight", target: null },
        { type: "click", target: trigger("subject_reference_style") },
        { type: "waitFor", target: current("subject_reference_style") },
        { type: "wait", ms: 3800 },
        { type: "moveTo", target: option("subject_reference_style", 3) },
        { type: "wait", ms: 700 },
        { type: "moveTo", target: option("subject_reference_style", 6) },
        { type: "wait", ms: 500 },
        // A second press on the trigger closes the menu without changing the value.
        { type: "click", target: trigger("subject_reference_style"), pointer: true },
        { type: "wait", ms: 300 },
      ],
    },
    {
      id: "formats",
      narration:
        "Date Format and Time Format set the style, like 2214 hours versus 10:14 PM. Include Topic Headings adds section headings to the draft, and Include Statutes takes your state.",
      actions: [
        { type: "scrollIntoView", target: field("date_format") },
        { type: "spotlight", target: field("date_format") },
        { type: "wait", ms: 1800 },
        { type: "spotlight", target: field("time_format") },
        { type: "wait", ms: 4800 },
        { type: "scrollIntoView", target: field("topic_headings") },
        { type: "spotlight", target: field("topic_headings") },
        { type: "wait", ms: 3800 },
        { type: "scrollIntoView", target: field("statute_state") },
        { type: "spotlight", target: field("statute_state") },
        { type: "wait", ms: 2500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "spellings",
      narration:
        "Speech recognition does not know your beat. Common Spellings is the list the AI uses when it writes. Add each name as you want it to appear, one word at a time, like Alvarado.",
      actions: [
        { type: "wait", ms: 2300 },
        { type: "scrollIntoView", target: field("common_spellings") },
        { type: "spotlight", target: field("common_spellings") },
        { type: "wait", ms: 5000 },
        { type: "spotlight", target: null },
        { type: "click", target: spellings },
        { type: "wait", ms: 1900 },
        { type: "type", target: spellings, text: "Alvarado,", cps: 8 },
        { type: "wait", ms: 300 },
      ],
    },
    {
      id: "partners",
      narration:
        "Start with your regular partners and supervisors, like Nguyen and Kowalczyk. They are in nearly every report, and the transcript gets them wrong more than anything else.",
      actions: [
        { type: "wait", ms: 3800 },
        { type: "click", target: spellings },
        { type: "type", target: spellings, text: "Nguyen,", cps: 10 },
        { type: "type", target: spellings, text: "Kowalczyk,", cps: 12 },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "save",
      narration:
        "Click Save Preferences. To check a change, open a recent case and choose Regenerate Narrative from the Narrative dropdown. Regenerating on a real transcript is the quickest way to see what a setting does.",
      actions: [
        { type: "moveTo", target: sel("preferencesSave") },
        { type: "wait", ms: 500 },
        { type: "click", target: sel("preferencesSave") },
        { type: "wait", ms: 1500 },
        { type: "click", target: `${sel("casesNav")} button` },
        { type: "waitFor", target: sel("reportScore") },
        { type: "waitFor", target: sel("editorTabNarrative") },
        { type: "wait", ms: 600 },
        { type: "click", target: sel("editorTabNarrative") },
        { type: "waitFor", target: sel("regenerateNarrative") },
        { type: "moveTo", target: sel("regenerateNarrative") },
        { type: "spotlight", target: sel("regenerateNarrative") },
        { type: "wait", ms: 3500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "push",
      narration:
        "One last thing. If your agency admin pushes department preferences, the pushed values overwrite yours at the time of the push, but your call sign is never pushed. And a report template overrides header, footer, and writing instructions, so if a template is selected, edit the template.",
      actions: [{ type: "wait", ms: 4000 }],
    },
  ],
};

export default scene;
